import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Paper,
  Divider
} from '@mui/material';
import {
  Refresh,
  FilterList,
  Visibility,
  Delete,
  Email,
  Phone,
  Person,
  CalendarToday,
  Close
} from '@mui/icons-material';
import AdminPageHeader from '../components/AdminPageHeader';
import contactService from '../services/contactService';

const ContactMessages = () => {
  // Estados principales
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });

  // Filtros
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    status: '',
    search: '',
    startDate: '',
    endDate: ''
  });

  // Modal de detalle
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [notes, setNotes] = useState('');

  // Mensaje de alerta
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadMessages();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.limit, filters.status]);

  const loadMessages = async () => {
    setLoading(true);
    const result = await contactService.list(filters);
    if (result.success) {
      setMessages(result.data);
      setPagination(result.pagination);
    } else {
      setMessage({ type: 'error', text: result.message });
    }
    setLoading(false);
  };

  const loadStats = async () => {
    const result = await contactService.getStats();
    if (result.success) {
      setStats(result.data);
    }
  };

  const handleSearch = () => {
    setFilters({ ...filters, page: 1 });
    loadMessages();
  };

  const handleViewDetail = async (msg) => {
    const result = await contactService.getById(msg.id);
    if (result.success) {
      setSelectedMessage(result.data);
      setNotes(result.data.notes || '');
      setOpenDetail(true);
      // Recargar lista para actualizar el estado
      loadMessages();
      loadStats();
    }
  };

  const handleCloseDetail = () => {
    setOpenDetail(false);
    setSelectedMessage(null);
    setNotes('');
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedMessage) return;
    
    setUpdatingStatus(true);
    const result = await contactService.updateStatus(selectedMessage.id, {
      status: newStatus,
      notes: notes || undefined
    });
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Estado actualizado correctamente' });
      setSelectedMessage({ ...selectedMessage, ...result.data });
      loadMessages();
      loadStats();
    } else {
      setMessage({ type: 'error', text: result.message });
    }
    setUpdatingStatus(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este mensaje?')) return;
    
    const result = await contactService.delete(id);
    if (result.success) {
      setMessage({ type: 'success', text: 'Mensaje eliminado correctamente' });
      loadMessages();
      loadStats();
      if (selectedMessage?.id === id) {
        handleCloseDetail();
      }
    } else {
      setMessage({ type: 'error', text: result.message });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      nuevo: 'error',
      leido: 'warning',
      respondido: 'success',
      archivado: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      nuevo: '🆕 Nuevo',
      leido: '👁️ Leído',
      respondido: '✅ Respondido',
      archivado: '📦 Archivado'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box className="admin-page">
      <AdminPageHeader
        eyebrow="CONSULTAS"
        title="Mensajes de contacto"
        subtitle="Organizá y respondé las consultas recibidas desde el sitio."
        icon={<Email />}
      />

      {message.text && (
        <Alert severity={message.type} className="mb-md" onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      {/* Estadísticas */}
      {stats && (
        <Grid container spacing={2} className="mb-lg">
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold">
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  📊 Total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'error.lighter' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color="error.dark">
                  {stats.por_estado.nuevos}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  🆕 Nuevos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'warning.lighter' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color="warning.dark">
                  {stats.por_estado.leidos}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  👁️ Leídos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'success.lighter' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color="success.dark">
                  {stats.por_estado.respondidos}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ✅ Respondidos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold">
                  {stats.recientes.hoy}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  📅 Hoy
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold">
                  {stats.recientes.esta_semana}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  📆 Esta Semana
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filtros */}
      <Card className="mb-md">
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Buscar"
                placeholder="Nombre, email o asunto..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filters.status}
                  label="Estado"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="nuevo">🆕 Nuevos</MenuItem>
                  <MenuItem value="leido">👁️ Leídos</MenuItem>
                  <MenuItem value="respondido">✅ Respondidos</MenuItem>
                  <MenuItem value="archivado">📦 Archivados</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={1}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<FilterList />}
                onClick={handleSearch}
              >
                Filtrar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabla de mensajes */}
      <Card>
        <CardContent>
          <Box className="d-flex justify-between align-center mb-md">
            <Typography variant="h6">
              Mensajes ({pagination.total})
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={() => { loadMessages(); loadStats(); }}
            >
              Recargar
            </Button>
          </Box>

          {loading ? (
            <Box className="d-flex justify-center align-center" sx={{ py: 8 }}>
              <CircularProgress />
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No hay mensajes para mostrar
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Teléfono</TableCell>
                      <TableCell>Asunto</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {messages.map((msg) => (
                      <TableRow key={msg.id} hover>
                        <TableCell>{formatDate(msg.createdAt)}</TableCell>
                        <TableCell>
                          <Box className="d-flex align-center gap-xs">
                            <Person fontSize="small" color="action" />
                            {msg.name}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <a href={`mailto:${msg.email}`} className="text-no-underline">
                            {msg.email}
                          </a>
                        </TableCell>
                        <TableCell>
                          {msg.phone ? (
                            <a href={`tel:${msg.phone}`} className="text-no-underline">
                              {msg.phone}
                            </a>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>{msg.subject || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(msg.status)}
                            color={getStatusColor(msg.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetail(msg)}
                            title="Ver detalle"
                          >
                            <Visibility />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(msg.id)}
                            title="Eliminar"
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Paginación */}
              <Box className="d-flex justify-center align-center gap-md" sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  disabled={pagination.page === 1}
                  onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
                >
                  ← Anterior
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Página {pagination.page} de {pagination.totalPages} ({pagination.total} mensajes)
                </Typography>
                <Button
                  variant="outlined"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
                >
                  Siguiente →
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal de Detalle */}
      <Dialog open={openDetail} onClose={handleCloseDetail} maxWidth="md" fullWidth>
        {selectedMessage && (
          <>
            <DialogTitle>
              <Box className="d-flex justify-between align-center">
                <Typography variant="h6">Detalle del Mensaje</Typography>
                <IconButton onClick={handleCloseDetail} size="small">
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                {/* Información del contacto */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box className="d-flex align-center gap-xs mb-sm">
                          <Person color="primary" />
                          <Typography variant="subtitle2" color="text.secondary">
                            Nombre:
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="600">
                          {selectedMessage.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box className="d-flex align-center gap-xs mb-sm">
                          <Email color="primary" />
                          <Typography variant="subtitle2" color="text.secondary">
                            Email:
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="600">
                          <a href={`mailto:${selectedMessage.email}`} className="text-no-underline">
                            {selectedMessage.email}
                          </a>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box className="d-flex align-center gap-xs mb-sm">
                          <Phone color="primary" />
                          <Typography variant="subtitle2" color="text.secondary">
                            Teléfono:
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="600">
                          {selectedMessage.phone ? (
                            <a href={`tel:${selectedMessage.phone}`} className="text-no-underline">
                              {selectedMessage.phone}
                            </a>
                          ) : (
                            'No proporcionado'
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box className="d-flex align-center gap-xs mb-sm">
                          <CalendarToday color="primary" />
                          <Typography variant="subtitle2" color="text.secondary">
                            Fecha:
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="600">
                          {formatDate(selectedMessage.createdAt)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Asunto */}
                {selectedMessage.subject && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Asunto:
                    </Typography>
                    <Typography variant="h6">
                      {selectedMessage.subject}
                    </Typography>
                  </Grid>
                )}

                {/* Mensaje */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Mensaje:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="body1" className="whitespace-pre-wrap">
                      {selectedMessage.message}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Estado actual */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Estado actual:
                  </Typography>
                  <Chip
                    label={getStatusLabel(selectedMessage.status)}
                    color={getStatusColor(selectedMessage.status)}
                    size="medium"
                  />
                </Grid>

                {/* Cambiar estado */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Cambiar estado:
                  </Typography>
                  <Box className="d-flex gap-sm flex-wrap">
                    <Button
                      variant={selectedMessage.status === 'nuevo' ? 'contained' : 'outlined'}
                      color="error"
                      size="small"
                      onClick={() => handleStatusChange('nuevo')}
                      disabled={updatingStatus || selectedMessage.status === 'nuevo'}
                    >
                      🆕 Nuevo
                    </Button>
                    <Button
                      variant={selectedMessage.status === 'leido' ? 'contained' : 'outlined'}
                      color="warning"
                      size="small"
                      onClick={() => handleStatusChange('leido')}
                      disabled={updatingStatus || selectedMessage.status === 'leido'}
                    >
                      👁️ Leído
                    </Button>
                    <Button
                      variant={selectedMessage.status === 'respondido' ? 'contained' : 'outlined'}
                      color="success"
                      size="small"
                      onClick={() => handleStatusChange('respondido')}
                      disabled={updatingStatus || selectedMessage.status === 'respondido'}
                    >
                      ✅ Respondido
                    </Button>
                    <Button
                      variant={selectedMessage.status === 'archivado' ? 'contained' : 'outlined'}
                      color="inherit"
                      size="small"
                      onClick={() => handleStatusChange('archivado')}
                      disabled={updatingStatus || selectedMessage.status === 'archivado'}
                    >
                      📦 Archivado
                    </Button>
                  </Box>
                </Grid>

                {/* Notas */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Notas internas (opcional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Agrega notas sobre este mensaje..."
                    disabled={updatingStatus}
                  />
                </Grid>

                {/* Información de respuesta */}
                {selectedMessage.respondedAt && (
                  <Grid item xs={12}>
                    <Alert severity="success">
                      <Typography variant="body2">
                        <strong>Respondido el:</strong> {formatDate(selectedMessage.respondedAt)}
                      </Typography>
                      {selectedMessage.responder && (
                        <Typography variant="body2">
                          <strong>Por:</strong> {selectedMessage.responder.fullName || selectedMessage.responder.username}
                        </Typography>
                      )}
                    </Alert>
                  </Grid>
                )}

                {/* Notas guardadas */}
                {selectedMessage.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Notas guardadas:
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.lighter' }}>
                      <Typography variant="body2" className="whitespace-pre-wrap">
                        {selectedMessage.notes}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleDelete(selectedMessage.id)} color="error">
                Eliminar
              </Button>
              <Button onClick={handleCloseDetail} variant="outlined">
                Cerrar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ContactMessages;

